/**
 * Time-to-live (TTL) cache implementation with optional size bounds.
 * Automatically expires entries after a specified duration and evicts
 * least-recently-used entries when max size is reached,
 * preventing unbounded memory growth in long-lived applications.
 */

export interface TTLCacheEntry<T> {
  data: T;
  expiresAt: number;
  lastAccessedAt: number;
}

export class TTLCache<T> {
  private cache: Map<string, TTLCacheEntry<T>>;
  private ttlMs: number;
  private maxSize: number | undefined;
  private cleanupIntervalMs: number;
  private cleanupTimer?: ReturnType<typeof setInterval>;

  constructor(
    ttlMs: number = 10 * 60 * 1000,
    maxSize?: number,
    cleanupIntervalMs: number = 60 * 1000,
  ) {
    this.cache = new Map();
    this.ttlMs = ttlMs;
    this.maxSize = maxSize;
    this.cleanupIntervalMs = cleanupIntervalMs;

    // Periodically clean up expired entries to prevent memory leak
    // in long-lived applications (e.g., SPAs, Electron apps).
    if (typeof window === "undefined" || typeof setInterval !== "undefined") {
      this.startCleanupTimer();
    }
  }

  /**
   * Get a value from the cache if it exists and hasn't expired.
   * Returns undefined if missing or expired.
   * Updates lastAccessedAt timestamp for LRU tracking.
   */
  get(key: string): T | undefined {
    const entry = this.cache.get(key);
    if (!entry) return undefined;

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return undefined;
    }

    // Update access time for LRU tracking
    entry.lastAccessedAt = Date.now();

    return entry.data;
  }

  /**
   * Set a value in the cache with automatic expiration and LRU eviction.
   * If cache is at max size, evicts the least-recently-used entry.
   */
  set(key: string, value: T): void {
    const now = Date.now();

    // If key already exists, update it
    if (this.cache.has(key)) {
      const entry = this.cache.get(key)!;
      entry.data = value;
      entry.expiresAt = now + this.ttlMs;
      entry.lastAccessedAt = now;
      return;
    }

    // If at max size, evict LRU entry before adding new one
    if (this.maxSize !== undefined && this.cache.size >= this.maxSize) {
      this.evictLRU();
    }

    this.cache.set(key, {
      data: value,
      expiresAt: now + this.ttlMs,
      lastAccessedAt: now,
    });
  }

  /**
   * Check if a key exists and hasn't expired.
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Delete a specific entry from the cache.
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all entries from the cache.
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get the current number of entries (including expired).
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Get the number of non-expired entries.
   */
  get validSize(): number {
    let count = 0;
    const now = Date.now();

    for (const entry of this.cache.values()) {
      if (now <= entry.expiresAt) {
        count++;
      }
    }

    return count;
  }

  /**
   * Evict the least-recently-used (oldest access time) entry.
   * Called automatically when cache reaches max size.
   */
  private evictLRU(): void {
    let lruKey: string | undefined;
    let lruTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessedAt < lruTime) {
        lruTime = entry.lastAccessedAt;
        lruKey = key;
      }
    }

    if (lruKey !== undefined) {
      this.cache.delete(lruKey);
    }
  }

  /**
   * Remove all expired entries.
   */
  private cleanup(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach((key) => this.cache.delete(key));
  }

  /**
   * Start the periodic cleanup timer.
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.cleanupIntervalMs);

    // Prevent the timer from keeping the process alive in Node.js
    const timer = this.cleanupTimer as unknown as { unref?: () => void };
    timer.unref?.();
  }

  /**
   * Stop the periodic cleanup timer (useful for cleanup or testing).
   */
  stopCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }
  }

  /**
   * Manually trigger cleanup.
   */
  forceCleanup(): void {
    this.cleanup();
  }
}
